import apiClient from './client';
import { accountManager } from '../../utils/accountManager';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface User {
  id: string;
  email: string;
  fullName?: string;
  roles: string[];
  permissions: string[];
  companyId: string | null;
  secondaryCompanyIds?: string[];
  status?: string;
}

export interface Company {
  id: string;
  name: string;
  slug: string;
  /** 'PRIMARY_ENTERPRISE' (internal) | 'PARTNER' (external supplier). */
  type?: string;
  enabledPlugins: string[];
  /** Backend-computed effective plugin list (partner-aware, per active-company context). */
  effectivePlugins?: string[];
  /**
   * Company-level settings from the admin company form. Returned in full by
   * `/api/auth/company`, `/tenant` and switch-company, and cached on the
   * session; plugins read `currency` off it via the plugin-sdk's
   * `getActiveCompanyCurrency()` to prefill forms. Only the fields consumers
   * actually use are declared.
   */
  settings?: {
    currency?: string;
    timezone?: string;
    language?: string;
    dateFormat?: string;
    numberFormat?: string;
  };
  /**
   * Brand assets (public URLs); absent when the company has none. Returned by
   * the same three endpoints as `settings` and cached on the session — see
   * `resolveCompanyBrand` for how the shell uses the platform pair.
   */
  branding?: {
    /** Logo the mailers put in email headers; not shown in the shell. */
    email?: string;
    /** Platform logo for the sidebar rail when the sidebar is light. */
    platformLight?: string;
    /** Platform logo for the sidebar rail when the sidebar is dark. */
    platformDark?: string;
    /** Primary colour the app takes for this company, `#rrggbb`. */
    primaryColor?: string;
    /** Dark-theme counterpart; absent → the dark theme keeps its default. */
    primaryColorDark?: string;
  };
}

/**
 * A completed sign-in. Also what the two-factor challenge returns, because a
 * sign-in that finished in two steps is the same session as one that finished
 * in one.
 */
export interface SessionPayload {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: User;
  /** Set when the second factor was a backup code, so the client can warn. */
  usedBackupCode?: boolean;
  backupCodesRemaining?: number;
}

/**
 * A sign-in that stopped halfway because the account has two-factor on.
 *
 * Carries NO tokens — nothing is signed in yet. `challengeToken` is the ticket
 * that proves the password step happened and is presented alongside a code.
 */
export interface TwoFactorChallenge {
  twoFactorRequired: true;
  challengeToken: string;
  /** Seconds until the ticket stops working. */
  expiresIn: number;
}

export interface LoginResponse {
  success: boolean;
  data?: SessionPayload | TwoFactorChallenge;
  error?: string;
}

/** Narrow a login response to the half-finished case. */
export const isTwoFactorChallenge = (
  data: LoginResponse['data']
): data is TwoFactorChallenge =>
  !!data && (data as TwoFactorChallenge).twoFactorRequired === true;

export interface RefreshResponse {
  success: boolean;
  data?: {
    accessToken: string;
    expiresIn: number;
  };
  error?: string;
}

export interface UserPreferences {
  theme?: 'light' | 'dark' | 'auto';
  notifications?: { email?: boolean; push?: boolean; sms?: boolean; mutedTypes?: string[] };
  display?: { density?: string; sidebarCollapsed?: boolean };
  [key: string]: unknown;
}

export interface NotificationMail {
  type: string;
  area: string;
  label: string;
  description: string;
  muted: boolean;
  mutable: boolean;
}

export interface AccessibleCompany {
  id: string;
  name: string;
  slug: string;
  type: string;
  status: string;
  isPrimary: boolean;
}

export interface AccessibleCompaniesResponse {
  success: boolean;
  data?: {
    companies: AccessibleCompany[];
  };
  error?: string;
}

export interface SwitchCompanyResponse {
  success: boolean;
  data?: {
    accessToken: string;
    refreshToken: string;
    expiresIn: number;
    user: User;
    company: Company;
  };
  error?: string;
}

export const authApi = {
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      const response = await apiClient.post<LoginResponse>('/api/auth/login', credentials);
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Login failed',
      };
    }
  },

  /**
   * End a session on the server: revokes the access token (Authorization
   * header) and the 7-day refresh token (body). Takes the session explicitly
   * so signing out a NON-active account, or all accounts, revokes the right
   * tokens — the interceptor would otherwise attach the active account's.
   * Defaults to the active session. Never throws: a failed revoke must not
   * stop the client from signing out locally.
   */
  async logout(session?: { accessToken?: string; refreshToken?: string }): Promise<void> {
    const target = session ?? accountManager.getActiveSession() ?? undefined;
    try {
      await apiClient.post(
        '/api/auth/logout',
        { refreshToken: target?.refreshToken },
        { headers: { Authorization: `Bearer ${target?.accessToken ?? ''}` } }
      );
    } catch (error) {
      console.error('Logout error:', error);
    }
  },

  async refreshToken(refreshToken: string): Promise<RefreshResponse> {
    try {
      const response = await apiClient.post<RefreshResponse>('/api/auth/refresh', {
        refreshToken,
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Token refresh failed',
      };
    }
  },

  async getCurrentUser(): Promise<User | null> {
    try {
      const response = await apiClient.get<{ success: boolean; data: User }>('/api/auth/me');
      return response.data.data;
    } catch (error) {
      return null;
    }
  },

  /**
   * The active company, distinguishing "there isn't one" from "we couldn't ask".
   *
   * Those are not the same answer and collapsing them into `null` is what made
   * a rate-limited entitlement check look like an entitlement of nothing: the
   * sidebar pruned itself to the sections it could still resolve and every
   * plugin route rendered the generic 404, for an admin holding `["*"]`
   * (QA ledger AUTH2-001, and the nine streams that reported its symptoms).
   *
   * `resolved: false` means ASK AGAIN. A platform admin legitimately has no
   * company and comes back `{ resolved: true, company: null }`.
   */
  async getCurrentCompanyResult(): Promise<
    { resolved: true; company: Company | null } | { resolved: false; status?: number }
  > {
    try {
      const response = await apiClient.get<{ success: boolean; data: Company | null }>('/api/auth/company');
      return { resolved: true, company: response.data.data };
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      return { resolved: false, ...(status !== undefined ? { status } : {}) };
    }
  },

  /**
   * The company, or null for either reason.
   *
   * Kept for callers that genuinely cannot act on the difference. Anything
   * that decides what the user may SEE should use `getCurrentCompanyResult`.
   */
  async getCurrentCompany(): Promise<Company | null> {
    const result = await authApi.getCurrentCompanyResult();
    return result.resolved ? result.company : null;
  },

  async changePassword(
    currentPassword: string,
    newPassword: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      await apiClient.post('/api/auth/change-password', {
        currentPassword,
        newPassword,
      });

      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || error.message || 'Failed to change password',
      };
    }
  },

  getPreferences: async (): Promise<UserPreferences> => {
    const res = await apiClient.get('/api/auth/preferences');
    return res.data.data.preferences ?? {};
  },

  updatePreferences: async (patch: UserPreferences): Promise<UserPreferences> => {
    const res = await apiClient.put('/api/auth/preferences', { preferences: patch });
    return res.data.data.preferences ?? {};
  },

  getNotificationMails: async (): Promise<NotificationMail[]> => {
    const res = await apiClient.get('/api/auth/notification-mails');
    return res.data.data.mails ?? [];
  },

  /**
   * Changes the signed-in user's own display name. Settings showed it as a
   * read-only field with no way to correct it (QA ledger AUTH-011).
   */
  updateProfile: async (fullName: string): Promise<{ fullName: string }> => {
    const res = await apiClient.put('/api/auth/profile', { fullName });
    return res.data.data;
  },

  async getAccessibleCompanies(): Promise<AccessibleCompaniesResponse> {
    try {
      const response = await apiClient.get<AccessibleCompaniesResponse>(
        '/api/auth/accessible-companies'
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to get accessible companies',
      };
    }
  },

  async switchCompany(targetCompanyId: string): Promise<SwitchCompanyResponse> {
    try {
      const response = await apiClient.post<SwitchCompanyResponse>(
        '/api/auth/switch-company',
        { targetCompanyId }
      );
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to switch company',
      };
    }
  },

  async forgotPassword(email: string): Promise<{ success: boolean; error?: string }> {
    try {
      await apiClient.post('/api/auth/forgot-password', { email });
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to send reset email',
      };
    }
  },

  async resetPassword(token: string, newPassword: string): Promise<{ success: boolean; error?: string }> {
    try {
      await apiClient.post('/api/auth/reset-password', { token, newPassword });
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to reset password',
      };
    }
  },
};

/**
 * Two-factor authentication.
 *
 * Enrolment is deliberately two calls: `setup` shows a secret, `enable` proves
 * the user's app holds it. Nothing is switched on in between, so a mis-scanned
 * QR is a retry rather than a lockout.
 */
export const twoFactorApi = {
  /** What the security screen renders. No secrets in the response. */
  async status(): Promise<TwoFactorStatus> {
    const response = await apiClient.get<{ success: boolean; data: TwoFactorStatus }>(
      '/api/auth/2fa/status'
    );
    return response.data.data;
  },

  /** Generate a secret and show it. Turns nothing on. */
  async setup(): Promise<TwoFactorSetup> {
    const response = await apiClient.post<{ success: boolean; data: TwoFactorSetup }>(
      '/api/auth/2fa/setup'
    );
    return response.data.data;
  },

  /** Confirm the secret with a live code. Returns the backup codes, once. */
  async enable(code: string): Promise<{ backupCodes: string[] }> {
    const response = await apiClient.post<{ success: boolean; data: { backupCodes: string[] } }>(
      '/api/auth/2fa/enable',
      { code }
    );
    return response.data.data;
  },

  /** Turn it off. The password is required, not just an open session. */
  async disable(password: string): Promise<void> {
    await apiClient.post('/api/auth/2fa/disable', { password });
  },

  /** Replace the whole set; the previous codes stop working immediately. */
  async regenerateBackupCodes(password: string): Promise<{ backupCodes: string[] }> {
    const response = await apiClient.post<{ success: boolean; data: { backupCodes: string[] } }>(
      '/api/auth/2fa/backup-codes',
      { password }
    );
    return response.data.data;
  },

  /** Finish a sign-in that stopped at the challenge. */
  async challenge(challengeToken: string, code: string): Promise<LoginResponse> {
    try {
      const response = await apiClient.post<LoginResponse>('/api/auth/2fa/challenge', {
        challengeToken,
        code,
      });
      return response.data;
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.error || 'Could not verify that code',
      };
    }
  },
};

export interface TwoFactorStatus {
  enabled: boolean;
  enabledAt: string | null;
  backupCodesRemaining: number;
  setupPending: boolean;
}

export interface TwoFactorSetup {
  /** Base32, for typing in by hand when a QR cannot be scanned. */
  secret: string;
  /** The `otpauth://` URI behind the QR code. */
  otpauthUri: string;
  issuer: string;
  account: string;
}
